<template>
  <div
    v-editable="blok"
    class="mobile-slider flipped"
    :class="[breakpointClass, removeSpacingClass]"
    :style="minWidth"
  >
    <div class="mobile-slider__content">
      <StoryblokComponent
        v-for="childBlok in blok.items"
        :key="childBlok._uid"
        class="mobile-slider__inner-content"
        :blok="childBlok"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SbMobileSliderStoryblok } from '@/types'

interface Props {
  blok: SbMobileSliderStoryblok
}
const props = defineProps<Props>()

const hideSpacings = computed(() => {
  return props.blok?.options?.includes('remove_spacing')
})

const breakpointClass = computed(
  () => `mobile-slider--${props.blok.breakpoint}`,
)
const removeSpacingClass = computed(
  () => hideSpacings.value && 'mobile-slider--remove-spacing',
)

const minWidth = computed(() => {
  return {
    '--mobile-min-width':
      `${props.blok.min_width_responsive?.mobile}px` || '100%',
    '--tablet-min-width':
      `${props.blok.min_width_responsive?.tablet}px` || '100%',
  }
})
</script>

<style lang="scss" scoped>
@mixin slider-values {
  --slider-margin: -20px;
  overflow-x: auto;
  margin-inline: var(--slider-margin);

  --outer-padding-content-top: 20px;
  --outer-padding-content-bottom: 64px;
  --inner-padding-content-left: 20px;
  --inner-padding-content-right: 20px;
}

.mobile-slider {
  --mobile-min-width: 0;
  --tablet-min-width: 0;
  --outer-padding-content-top: 0;
  --outer-padding-content-bottom: 0;
  --inner-padding-content-left: 0;
  --inner-padding-content-right: 0;

  scrollbar-color: var(--navy-600) transparent;
  scrollbar-width: thin;
  margin-inline: var(--slider-margin, 0);
  height: 100%;

  &__content {
    margin-top: var(--space24);
    margin-bottom: var(--space64);
    padding-block: var(--outer-padding-content-top)
      var(--outer-padding-content-bottom);
  }

  &__inner-content {
    padding-inline: var(--inner-padding-content-left)
      var(--inner-padding-content-right);
    min-width: var(--mobile-min-width);

    @media (min-width: $breakpoint-md) {
      min-width: var(--tablet-min-width);
    }

    @media (min-width: $breakpoint-lg) {
      min-width: 0;
    }
  }

  &::-webkit-scrollbar {
    display: block;
    height: 9px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--navy-600);
    border-right: none;
    border-left: none;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    margin-left: var(--space24);
    margin-right: var(--space24);
    background: var(--gray-400);
    border-radius: 4px;

    @media (min-width: $breakpoint-md - 1px) {
      margin-left: var(--space128);
      margin-right: var(--space128);
    }
  }

  &--md {
    @media (max-width: $breakpoint-md) {
      @include slider-values;
    }
  }

  &--lg {
    @media (max-width: $breakpoint-lg - 1px) {
      @include slider-values;

      @media (min-width: $breakpoint-md) {
        --slider-margin: -128px;
        --inner-padding-content-left: 128px;
        --inner-padding-content-right: 128px;
      }
    }
  }

  &--remove-spacing {
    --slider-margin: 0;
  }
}

.flipped,
.flipped .mobile-slider__content {
  transform: rotateX(180deg);
}
</style>
